import { useState } from "react";

import { useCpuRunTasks } from "../../api/queries";
import type { CpuRun, CpuTaskState } from "../../api/types";
import { CPU_TASK_PAGE_SIZE } from "../../config";
import {
  summarizeTaskRow,
  taskLogLabel,
  taskStateLabel,
  taskTone,
} from "../../lib/cpu";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../ui/StateBlock";

/** What the chips filter to; `all` is the absence of the `state` parameter. */
type TaskFilter = CpuTaskState | "all";

/** The chips, in the order somebody triaging reads them. */
const TASK_FILTERS: readonly TaskFilter[] = [
  "failed",
  "running",
  "waiting",
  "succeeded",
  "not_run",
  "all",
];

/** The word on a chip: the state's own, plus the one that is not a state. */
function filterLabel(filter: TaskFilter): string {
  return filter === "all" ? "All" : taskStateLabel(filter);
}

/** What the rows under a chip are called, counted. */
function filterNoun(filter: TaskFilter, count: number): string {
  const one = count === 1;
  switch (filter) {
    case "failed":
      return one ? "failed task" : "failed tasks";
    case "running":
      return one ? "running task" : "running tasks";
    case "waiting":
      return one ? "waiting task" : "waiting tasks";
    case "succeeded":
      return one ? "finished task" : "finished tasks";
    case "not_run":
      return one ? "task that never started" : "tasks that never started";
    case "all":
      return one ? "task" : "tasks";
  }
}

/** What an empty page means, which is not the same thing under every chip. */
function emptyLabel(filter: TaskFilter): string {
  switch (filter) {
    case "failed":
      return "No task of this run has failed.";
    case "running":
      return "No task of this run is running right now.";
    case "waiting":
      return "No task of this run is waiting for cores.";
    case "succeeded":
      return "No task of this run has finished yet.";
    case "not_run":
      return "Every task of this run was given a machine.";
    case "all":
      return "This run has no task to show yet.";
  }
}

interface TaskListProps {
  run: CpuRun;
  /** Show one task's log, in the single log panel this page has. */
  onShowLog: (index: number) => void;
}

/**
 * The tasks of a run, failed first, because that is why anybody opens it.
 *
 * It answers the question the counters cannot: *which* ones failed, on which
 * machine, with which exit code — and it answers it from the console's own
 * record rather than from the cluster, so it keeps answering the morning
 * after, when the machines have long gone back.
 *
 * Three things about it are the contract's rather than this component's. The
 * page is a **snapshot**: rows move while they are read, so a task may appear
 * twice or not at all across one walk, and anything that has to be exact is
 * the counter on the run above. Paging is **keyset on the index**, never an
 * offset, so a page does not shift under a run whose rows are changing. And
 * `total` counts the rows matching *this chip*, never the run's task count.
 *
 * The log button sets the page's own task number rather than opening a second
 * log panel: two places showing one task's output can disagree, and one of
 * them would be wrong.
 *
 * Args:
 *     run: The run as served by the API.
 *     onShowLog: Called with the task index whose log to show.
 *
 * Returns:
 *     The filter chips, the rows, and the way to ask for more of them.
 */
export function TaskList({ run, onShowLog }: TaskListProps): JSX.Element {
  // Failed first whenever anything failed: that is the only reason this list
  // is opened at all. It is the opening chip, never a chip somebody cannot
  // leave.
  const [filter, setFilter] = useState<TaskFilter>(() =>
    run.tasks_failed > 0 ? "failed" : "all",
  );
  const tasks = useCpuRunTasks(
    run.id,
    filter === "all" ? undefined : filter,
    run.finished_at === null,
  );

  const pages = tasks.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.items);
  const total = pages[0]?.total ?? 0;

  return (
    <>
      <div
        className="choice-row"
        role="radiogroup"
        aria-label="Which tasks to show"
      >
        {TASK_FILTERS.map((candidate) => (
          <label
            key={candidate}
            className="choice"
            data-selected={filter === candidate}
          >
            <input
              type="radio"
              name="task-filter"
              className="sr-only"
              value={candidate}
              checked={filter === candidate}
              onChange={() => setFilter(candidate)}
            />
            {filterLabel(candidate)}
          </label>
        ))}
      </div>

      {tasks.isPending ? (
        <LoadingBlock label="Reading the tasks…" />
      ) : tasks.isError ? (
        <ErrorBlock error={tasks.error} onRetry={() => void tasks.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBlock label={emptyLabel(filter)} />
      ) : (
        <>
          <ul className="job-list">
            {rows.map((task) => (
              <li key={task.index} className="job-row">
                <span className="job-row__name mono">task {task.index}</span>
                <span className="badge" data-tone={taskTone(task.state)}>
                  <span className="badge__dot" aria-hidden="true" />
                  {taskStateLabel(task.state)}
                </span>
                <span className="job-row__meta">{summarizeTaskRow(task)}</span>
                {task.log === "kept" ? (
                  <button
                    type="button"
                    className="btn btn--xs"
                    onClick={() => onShowLog(task.index)}
                  >
                    {`${taskLogLabel(task.log)} of task ${task.index}`}
                  </button>
                ) : (
                  <span className="job-row__meta">
                    {taskLogLabel(task.log)}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="capacity__note">
            {`Showing ${rows.length} of ${total} ${filterNoun(filter, total)}. ` +
              "What is on this page was true when it was built — a run's rows " +
              "move while they are read, and the counters above are the exact " +
              "answer."}
          </p>
          {tasks.hasNextPage ? (
            <button
              type="button"
              className="btn btn--sm"
              disabled={tasks.isFetchingNextPage}
              onClick={() => void tasks.fetchNextPage()}
            >
              {tasks.isFetchingNextPage
                ? "Reading…"
                : `Show ${CPU_TASK_PAGE_SIZE} more`}
            </button>
          ) : null}
        </>
      )}
    </>
  );
}
